import React, { useState, useEffect } from 'react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { useNotifications } from '../hooks/useNotifications';
import {
  Bell,
  Mail,
  MessageSquare,
  Phone,
  AlertTriangle,
  Save,
  RefreshCw,
  Info
} from 'lucide-react';

interface NotificationPreferences {
  notificationEmail: string;
  notificationPhone: string;
  receiveEmails: boolean;
  receiveTexts: boolean;
}

export const NotificationPreferences: React.FC = () => {
  const user = useUser();
  const { addNotification } = useNotifications();
  
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    notificationEmail: '',
    notificationPhone: '',
    receiveEmails: true,
    receiveTexts: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load current preferences
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/notification-preferences');
      
      if (response.success) {
        setPreferences(response.data);
      } else {
        addNotification({ type: 'error', title: 'Failed to load notification preferences' });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      addNotification({ type: 'error', title: 'Failed to load notification preferences' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate email if provided
    if (preferences.notificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(preferences.notificationEmail)) {
      newErrors.notificationEmail = 'Please enter a valid email address';
    }

    // Validate phone if provided
    if (preferences.notificationPhone && !/^\+?[\d\s\-\(\)]{10,}$/.test(preferences.notificationPhone)) {
      newErrors.notificationPhone = 'Please enter a valid phone number (10+ digits)';
    }

    // Warn if SMS is enabled but no phone number
    if (preferences.receiveTexts && !preferences.notificationPhone) {
      newErrors.notificationPhone = 'Phone number required to receive text notifications';
    }

    // Warn if emails are enabled but no email
    if (preferences.receiveEmails && !preferences.notificationEmail) {
      newErrors.notificationEmail = 'Email address required to receive email notifications';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.put('/api/notification-preferences', preferences);
      
      if (response.success) {
        addNotification({ type: 'success', title: 'Notification preferences updated successfully' });
        setPreferences(response.data);
      } else {
        addNotification({ type: 'error', title: response.error || 'Failed to update preferences' });
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      addNotification({ type: 'error', title: 'Failed to update notification preferences' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof NotificationPreferences, value: string | boolean) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-3" />
          <span className="text-gray-600">Loading notification preferences...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center">
          <Bell className="h-6 w-6 text-blue-600 mr-3" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
            <p className="text-sm text-gray-600">Configure how you receive notifications from Bloom Medical</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Email Notifications */}
        <div className="space-y-4">
          <div className="flex items-center">
            <Mail className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-md font-medium text-gray-900">Email Notifications</h3>
          </div>
          
          <div className="ml-7 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={preferences.notificationEmail}
                onChange={(e) => handleInputChange('notificationEmail', e.target.value)}
                placeholder="Enter email address for notifications"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.notificationEmail ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.notificationEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.notificationEmail}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Leave empty to use your account email: {user?.email}
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="receiveEmails"
                checked={preferences.receiveEmails}
                onChange={(e) => handleInputChange('receiveEmails', e.target.checked)}
                disabled={true} // Disabled until AWS SES is implemented
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="receiveEmails" className="ml-2 text-sm text-gray-900">
                Receive email notifications
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Coming Soon
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* SMS Notifications */}
        <div className="space-y-4">
          <div className="flex items-center">
            <MessageSquare className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-md font-medium text-gray-900">SMS Notifications</h3>
          </div>
          
          <div className="ml-7 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  value={preferences.notificationPhone}
                  onChange={(e) => handleInputChange('notificationPhone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.notificationPhone ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.notificationPhone && (
                <p className="mt-1 text-sm text-red-600">{errors.notificationPhone}</p>
              )}
              
              {/* SMS Rates Warning */}
              <div className="mt-2 flex items-start p-3 bg-yellow-50 rounded-md border border-yellow-200">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-yellow-800">
                  <p className="font-medium">Message and data rates may apply</p>
                  <p>Standard SMS charges from your carrier may apply for text notifications. You can opt out at any time.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="receiveTexts"
                checked={preferences.receiveTexts}
                onChange={(e) => handleInputChange('receiveTexts', e.target.checked)}
                disabled={true} // Disabled until SMS provider is implemented
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="receiveTexts" className="ml-2 text-sm text-gray-900">
                Receive SMS notifications
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Coming Soon
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="flex items-start p-4 bg-blue-50 rounded-md border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Notification Settings Saved</p>
            <p>Your preferences are saved and will be used when email and SMS notifications are enabled. You'll receive notifications for payouts, order updates, and account changes.</p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Preferences
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
